import React from 'react';
import { BarChart3, CheckCircle2, Info } from 'lucide-react';

const MetricsCard = ({ metrics, noBorder = false }) => {
  if (!metrics) return null;

  const accuracy = metrics.accuracy ?? metrics.accuracy_score ?? 0;
  const fluency = metrics.fluency ?? metrics.style_alignment ?? 0;
  const conciseness = metrics.conciseness ?? metrics.similarity_score ?? 0;
  const completeness = metrics.completeness ?? 0;

  const metricItems = [
    { label: 'Accuracy', value: accuracy, color: 'bg-green-500', description: 'Fact representation' },
    { label: 'Fluency', value: fluency, color: 'bg-blue-500', description: 'Language naturalness' },
    { label: 'Conciseness', value: conciseness, color: 'bg-purple-500', description: 'Brief and informative' },
    { label: 'Completeness', value: completeness, color: 'bg-amber-500', description: 'Key points coverage' },
  ];

  // Calculate overall score if missing
  const calculatedOverall = (accuracy + fluency + conciseness + completeness) / 4;
  const overallScore = metrics.overall_score || calculatedOverall || 0;

  return (
    <div className={`bg-white dark:bg-neutral-950 rounded-lg ${noBorder ? '' : 'border border-gray-200 dark:border-neutral-800 shadow-sm'} relative`}>
      <div className="p-2 border-b border-gray-200 dark:border-neutral-800 bg-gray-50/50 dark:bg-neutral-900/50 flex flex-col gap-3 rounded-t-lg">
        <h2 className="text-md font-semibold text-gray-900 dark:text-neutral-100 flex items-center">
          <BarChart3 className="w-5 h-5 mr-2 text-sky-600" />
          Evaluation Metrics
        </h2>
        
        <div className="flex items-center space-x-2">
          <div className="flex items-center w-fit space-x-2 bg-sky-50 dark:bg-sky-900/20 px-3 py-1 rounded-full border border-sky-100 dark:border-sky-800 text-xs">
            <span className="font-medium text-sky-700 dark:text-sky-300">Overall Score:</span>
            <span className="font-bold text-sky-600 dark:text-sky-400">{overallScore.toFixed(1)}/10</span>
          </div>
          <div className="group relative">
            <Info className="h-3.5 w-3.5 text-gray-400 cursor-help hover:text-sky-500 transition-colors" />
            <div className="absolute right-0 top-full mt-2 hidden group-hover:block w-64 p-3 bg-gray-900 dark:bg-neutral-800 text-white dark:text-neutral-100 text-[11px] rounded-lg shadow-xl z-[100]">
              <p className="mb-1 font-semibold text-sky-400 text-xs">How is this calculated?</p>
              <p className="leading-relaxed opacity-90 text-[10px]">
                The Overall Score is the average of the four evaluation metrics: Accuracy, Fluency, Conciseness, and Completeness.
              </p>
              <div className="absolute top-0 right-2 -translate-y-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-b-[6px] border-b-gray-900 dark:border-b-neutral-800"></div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="p-4">
        <div className="grid grid-cols-1 gap-6">
          {metricItems.map((item) => (
            <div key={item.label} className="space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-semibold text-gray-700 dark:text-neutral-300">{item.label}</span>
                  <p className="text-[10px] text-gray-400 uppercase tracking-tight">{item.description}</p>
                </div>
                <span className="text-sm font-bold text-gray-900 dark:text-neutral-100">{(item.value || 0).toFixed(1)}</span>
              </div>
              <div className="h-2 w-full bg-gray-100 dark:bg-neutral-800 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${item.color} transition-all duration-1000 ease-out`}
                  style={{ width: `${(item.value || 0) * 10}%` }}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 p-2 bg-sky-50/50 dark:bg-sky-900/5 border border-sky-100 dark:border-sky-900/30 rounded-lg">
          <div className="flex items-start space-x-3">
            <CheckCircle2 className="w-5 h-5 text-sky-500 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-sky-900 dark:text-sky-300">Evaluation Insight</h4>
              <p className="text-xs text-sky-700/70 dark:text-sky-400/70 leading-relaxed mt-1">
                These metrics are generated by an LLM content evaluator comparing the AI output against the human reference. 
                A score above 8.0 indicates high alignment with the source of truth.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetricsCard;

